import "firebase/compat/firestore";

import firebase from "firebase/compat/app";
import { merge } from "lodash";
import { isEqual, sortBy } from "lodash";
import Vue from "vue";
import { firestoreAction } from "vuexfire";

import { PerformanceStatistic } from "@/assets/constants/perfStats";
import axios from "@/http/axios";
import { getNested } from "@/store/utils";

const state = {
  details: undefined,
  benchmarks: [],
  validDates: undefined,
  performanceData: undefined,
  performanceWithPeriodData: undefined,
  returnsVar: undefined,
  riskReturn: undefined,
  cumulativeReturns: undefined,
  productPrices: undefined,
  benchmarkPrices: {},
  lastCumReturns: {},
  biggestDrawdowns: undefined,
  drawdowns: undefined,
  outperformance: undefined,
  volatility: undefined,
  annualizedReturnRolling: undefined,
  maxDrawdownRolling: undefined,
  yearlyCumulativeReturns: undefined,
  monthlyCumulativeReturns: undefined,
  returns: undefined,
  holdingsDetails: {},
  holdingDataPropertyMetadata: undefined,
  holdingDataPropertyCategoriesTranslations: undefined,
  liquidityAnalysisHoldings: undefined,
  holdingsTable: {},
  portfolioBreakdownsOnDate: {},
  portfolioBreakdownsOverTime: {},
  assetsUnderManagement: undefined,
  performanceTableData: undefined,
  lastLivePrice: undefined,
  // Note that productConfig works the other way for error detection,
  // null being the base state and undefined the error state.
  productConfig: null,
  bindProductConfigPromise: null,
  viewsConfigPerLang: {},
  bindViewsConfigPromisePerLang: {},
};

const getters = {
  isProductBindingReady: (state) => (langsSubset) => {
    if (langsSubset == undefined) {
      return undefined;
    }

    return (
      state.productConfig != null &&
      isEqual(sortBy(langsSubset), sortBy(Object.keys(state.viewsConfigPerLang)))
    );
  },
  getDetails: (state) => state.details,
  getBenchmarks: (state) => {
    return state.benchmarks;
  },
  getValidDates: (state) => state.validDates,

  getPerformanceStats: (state) => state.performanceData,
  getPerformanceWithPeriodStats: (state) => state.performanceWithPeriodData,
  getReturnsVar: (state) => state.returnsVar,
  getRiskReturn: (state) => state.riskReturn,
  getCumulativeReturns: (state) => state.cumulativeReturns,
  getProductPrices: (state) => {
    return state.productPrices;
  },
  getBenchmarkPrices:
    (state) =>
    ({ benchmarkSymbol }) => {
      return getNested(state.benchmarkPrices, benchmarkSymbol);
    },
  getLastCumReturns:
    (state) =>
    ({ period }) => {
      return getNested(state.lastCumReturns, period);
    },
  getDrawdowns: (state) => state.drawdowns,
  getBiggestDrawdowns: (state) => state.biggestDrawdowns,
  getOutperformanceValues: (state) => state.outperformance,
  getVolatilityValues: (state) => state.volatility,
  getAnnualizedReturnRollingValues: (state) => state.annualizedReturnRolling,
  getMaxDrawdownRollingValues: (state) => state.maxDrawdownRolling,
  getYearlyCumulativeReturns: (state) => state.yearlyCumulativeReturns,
  getMonthlyCumulativeReturns: (state) => state.monthlyCumulativeReturns,
  getReturns: (state) => state.returns,
  getHoldingsDetails: (state) => {
    return state.holdingsDetails;
  },
  getLiquidityAnalysisHoldings: (state) => {
    return state.liquidityAnalysisHoldings;
  },
  getHoldingDataPropertyMetadata: (state) => {
    return state.holdingDataPropertyMetadata;
  },
  getHoldingDataPropertyCategoriesTranslations: (state) => {
    return state.holdingDataPropertyCategoriesTranslations;
  },
  getHoldingsTable:
    (state) =>
    ({ holdingComponentId }) => {
      return getNested(state.holdingsTable, holdingComponentId);
    },
  getPortfolioBreakdownOnDate:
    (state) =>
    ({ holdingComponentId }) => {
      return getNested(state.portfolioBreakdownsOnDate, holdingComponentId);
    },
  getPortfolioBreakdownOverTime:
    (state) =>
    ({ holdingComponentId }) => {
      return getNested(state.portfolioBreakdownsOverTime, holdingComponentId);
    },
  getAssetsUnderManagement: (state) => {
    return state.assetsUnderManagement;
  },
  getPerformanceTableData: (state) => {
    return state.performanceTableData;
  },
  getLastLivePrice: (state) => state.lastLivePrice,
  getAdditionalFollowers: (state) => {
    if (state.productConfig == undefined) {
      return state.productConfig;
    }

    return state.productConfig.additionalFollowers ?? [];
  },
  getProductVisibility: (state) => {
    return state.productConfig?.general.visibility;
  },
  getProductConfig: (state) => (lang, fallbackLang) => {
    if (state.productConfig == undefined) {
      return state.productConfig;
    }

    if (
      state.viewsConfigPerLang[lang] == undefined &&
      state.viewsConfigPerLang[fallbackLang] == undefined
    ) {
      return undefined;
    }

    // Little helper function to remove null values, hence avoiding
    // cases where we have an element in the global section (e.g.
    // general: {key1: value1}) and a null element in the lang section
    // (e.g. general: null) which would result in an empty key after
    // merge (e.g. general:null instead of general: {key1: value1}).
    const removeNullValues = (obj) => {
      if (!obj) return obj;

      return Object.fromEntries(Object.entries(obj).filter(([_, v]) => v != null));
    };

    // Try to get the views config in the requested 'lang'. If we can't,
    // try to get it in the 'fallbackLang'.
    const viewsConfig = state.viewsConfigPerLang[lang] ?? state.viewsConfigPerLang[fallbackLang];
    const configPerView = {};
    for (const viewConfig of viewsConfig) {
      configPerView[viewConfig.id] = viewConfig;
    }

    // Note that 'merge' mutate the first object, it's the source.
    // Therefore if we put one of the sate.productConfig, we get a
    // vuex error that we try to mutate an object outside a mutation.
    return merge(
      {},
      removeNullValues(state.productConfig),
      removeNullValues({ views: configPerView ?? {} })
    );
  },
  getViewsConfigPerLang: (state) => (langsSubset) => {
    if (langsSubset == undefined) {
      return undefined;
    }

    if (!langsSubset.every((lang) => state.viewsConfigPerLang.hasOwnProperty(lang))) {
      return undefined;
    }

    const toReturn = {};
    for (const [lang, viewsConfig] of Object.entries(state.viewsConfigPerLang)) {
      if (langsSubset.includes(lang)) {
        toReturn[lang] = {};
        for (const viewConfig of viewsConfig) {
          toReturn[lang][viewConfig.id] = viewConfig;
        }
      }
    }

    return toReturn;
  },
};

const mutations = {
  FETCH_DETAILS_SUCCESS: (state, { data }) => {
    state.details = data;
  },
  FETCH_BENCHMARKS_SUCCESS: (state, { data }) => {
    state.benchmarks = data;
  },
  FETCH_VALID_DATES_SUCCESS: (state, { data }) => {
    state.validDates = data;
  },
  FETCH_PERFORMANCE_DATA_SUCCESS: (state, { data }) => {
    state.performanceData = data;
  },
  FETCH_PERFORMANCE_WITH_PERIOD_DATA_SUCCESS: (state, { data }) => {
    state.performanceWithPeriodData = data;
  },
  FETCH_RETURN_VAR_SUCCESS: (state, { data }) => {
    state.returnsVar = data;
  },
  FETCH_RISK_RETURN_SUCCESS: (state, { data }) => {
    state.riskReturn = data;
  },
  FETCH_CUMULATIVE_RETURNS_SUCCESS: (state, { data }) => {
    state.cumulativeReturns = data;
  },
  FETCH_PRODUCT_PRICES_SUCCESS: (state, { data }) => {
    state.productPrices = data;
  },
  FETCH_BENCHMARK_PRICES_SUCCESS: (state, { benchmarkSymbol, data }) => {
    Vue.set(state.benchmarkPrices, benchmarkSymbol, data);
  },
  FETCH_LAST_CUMULATIVE_RETURNS_SUCCESS: (state, { period, data }) => {
    if (!state.lastCumReturns) {
      state.lastCumReturns = {};
    }
    if (!data) {
      Vue.set(state.lastCumReturns, period, data);
    } else {
      Vue.set(state.lastCumReturns, period, data[0]);
    }
  },
  FETCH_DRAWDOWNS_SUCCESS: (state, { data }) => {
    state.drawdowns = data;
  },
  FETCH_BIGGEST_DRAWDOWNS_SUCCESS: (state, { data }) => {
    state.biggestDrawdowns = data;
  },
  FETCH_OUTPERFORMANCE_SUCCESS: (state, { data }) => {
    state.outperformance = data;
  },
  FETCH_VOLATILITY_SUCCESS: (state, { data }) => {
    state.volatility = data;
  },
  FETCH_ANNUALIZED_RETURN_ROLLING_SUCCESS: (state, { data }) => {
    state.annualizedReturnRolling = data;
  },
  FETCH_MAX_DRAWDOWN_ROLLING_SUCCESS: (state, { data }) => {
    state.maxDrawdownRolling = data;
  },
  FETCH_YEARLY_CUMULATIVE_RETURNS_SUCCESS: (state, { data }) => {
    state.yearlyCumulativeReturns = data;
  },
  FETCH_MONTHLY_CUMULATIVE_RETURNS_SUCCESS: (state, { data }) => {
    state.monthlyCumulativeReturns = data;
  },
  FETCH_RETURNS_SUCCESS: (state, { data }) => {
    state.returns = data;
  },
  FETCH_HOLDINGS_DETAILS_SUCCESS: (state, { data }) => {
    state.holdingsDetails = data;
  },
  FETCH_LIQUIDITY_ANALYSIS_HOLDINGS_SUCCESS: (state, { data }) => {
    state.liquidityAnalysisHoldings = data;
  },
  FETCH_HOLDING_DATA_PROPERTY_METADATA_SUCCESS: (state, { data }) => {
    state.holdingDataPropertyMetadata = data;
  },
  FETCH_HOLDING_DATA_PROPERTY_CATEGORIES_TRANSLATIONS_SUCCESS: (state, { data }) => {
    state.holdingDataPropertyCategoriesTranslations = data;
  },
  FETCH_HOLDINGS_TABLE_SUCCESS: (state, { data, holdingComponentId }) => {
    Vue.set(state.holdingsTable, holdingComponentId, data);
  },
  TRANSFER_HOLDINGS_TABLE: (state, { fromHoldingComponentId, toHoldingComponentId }) => {
    Vue.set(state.holdingsTable, toHoldingComponentId, state.holdingsTable[fromHoldingComponentId]);
    Vue.delete(state.holdingsTable, fromHoldingComponentId);
  },
  FETCH_PORTFOLIO_BREAKDOWN_ON_DATE_SUCCESS: (state, { data, holdingComponentId }) => {
    Vue.set(state.portfolioBreakdownsOnDate, holdingComponentId, data);
  },
  TRANSFER_PORTFOLIO_BREAKDOWN_ON_DATE: (
    state,
    { fromHoldingComponentId, toHoldingComponentId }
  ) => {
    Vue.set(
      state.portfolioBreakdownsOnDate,
      toHoldingComponentId,
      state.portfolioBreakdownsOnDate[fromHoldingComponentId]
    );
    Vue.delete(state.portfolioBreakdownsOnDate, fromHoldingComponentId);
  },
  FETCH_PORTFOLIO_BREAKDOWN_OVER_TIME_SUCCESS: (state, { data, holdingComponentId }) => {
    Vue.set(state.portfolioBreakdownsOverTime, holdingComponentId, data);
  },
  TRANSFER_PORTFOLIO_BREAKDOWN_OVER_TIME: (
    state,
    { fromHoldingComponentId, toHoldingComponentId }
  ) => {
    Vue.set(
      state.portfolioBreakdownsOverTime,
      toHoldingComponentId,
      state.portfolioBreakdownsOverTime[fromHoldingComponentId]
    );
    Vue.delete(state.portfolioBreakdownsOverTime, fromHoldingComponentId);
  },
  FETCH_ASSETS_UNDER_MANAGEMENT_SUCCESS: (state, { data }) => {
    state.assetsUnderManagement = data;
  },
  FETCH_PERFORMANCE_TABLE_DATA_SUCCESS: (state, { data }) => {
    state.performanceTableData = data;
  },
  FETCH_LAST_LIVE_PRICE_SUCCESS: (state, { data }) => {
    state.lastLivePrice = data;
  },
  FETCH_PRODUCT_CONFIG_FAILURE: (state) => {
    state.productConfig = undefined;
  },
  SET_BIND_PRODUCT_CONFIG_PROMISE: (state, promise) => {
    state.bindProductConfigPromise = promise;
  },
  SET_BIND_VIEWS_CONFIG_PER_LANG_PROMISE: (state, { promise, lang }) => {
    Vue.set(state.bindViewsConfigPromisePerLang, lang, promise);
  },
  SET_VIEWS_CONFIG_PER_LANG_FROM_TEMP_KEY: (state, { lang, tempStateKey }) => {
    Vue.set(state.viewsConfigPerLang, lang, state[tempStateKey]);
    Vue.delete(state, tempStateKey);
  },
  RESET_ALL_STATES: (state) => {
    // Reset all the data fetched from the backend.
    state.details = undefined;
    state.benchmarks = [];
    state.validDates = undefined;
    state.performanceData = undefined;
    state.performanceWithPeriodData = undefined;
    state.returnsVar = undefined;
    state.riskReturn = undefined;
    state.cumulativeReturns = undefined;
    state.lastCumReturns = {};
    state.biggestDrawdowns = undefined;
    state.drawdowns = undefined;
    state.outperformance = undefined;
    state.volatility = undefined;
    state.annualizedReturnRolling = undefined;
    state.maxDrawdownRolling = undefined;
    state.yearlyCumulativeReturns = undefined;
    state.monthlyCumulativeReturns = undefined;
    state.returns = undefined;
    state.holdingsTable = {};
    state.holdingDataPropertyMetadata = undefined;
    state.holdingDataPropertyCategoriesTranslations = undefined;
    state.liquidityAnalysisHoldings = undefined;
    state.portfolioBreakdownsOnDate = {};
    state.portfolioBreakdownsOverTime = {};
    state.assetsUnderManagement = undefined;
    state.performanceTableData = undefined;
    state.lastLivePrice = undefined;
  },
};

const actions = {
  async fetchDetails({ commit }, { productId, benchmarkSymbol }) {
    const params = {};
    if (benchmarkSymbol !== undefined) {
      params["benchmark_symbol"] = benchmarkSymbol;
    }
    try {
      const response = await axios.get(`/api/products/${productId}/details`, {
        params: params,
      });
      commit("FETCH_DETAILS_SUCCESS", {
        data: response.data,
      });
    } catch (error) {
      commit("FETCH_DETAILS_SUCCESS", {
        data: null,
      });
      // We rethrow the error for sentry to catch it.
      throw error;
    }
  },
  async fetchBenchmarks({ commit }, { productId }) {
    const params = {};
    try {
      const response = await axios.get(`/api/products/${productId}/benchmarks`, {
        params: params,
      });
      commit("FETCH_BENCHMARKS_SUCCESS", {
        data: response.data,
      });
    } catch (error) {
      commit("FETCH_BENCHMARKS_SUCCESS", {
        data: null,
      });
      // We rethrow the error for sentry to catch it.
      throw error;
    }
  },
  async fetchValidDates({ commit }, { productId, period }) {
    const params = {};
    if (period) {
      params["period_start_date"] = period.start;
      params["period_end_date"] = period.end;
    }
    try {
      const response = await axios.get(`/api/products/${productId}/valid-dates`, {
        params: params,
      });
      commit("FETCH_VALID_DATES_SUCCESS", {
        data: response.data,
      });
    } catch (error) {
      commit("FETCH_VALID_DATES_SUCCESS", {
        data: null,
      });
      // We rethrow the error for sentry to catch it.
      throw error;
    }
  },
  async fetchLastCumulativeReturns({ commit }, { productId, period }) {
    const params = {};
    if (period !== undefined) {
      params["period"] = period;
    }
    try {
      const response = await axios.get(`/api/products/${productId}/last-cumulative-returns`, {
        params: params,
      });
      commit("FETCH_LAST_CUMULATIVE_RETURNS_SUCCESS", {
        period,
        data: response.data,
      });
    } catch (error) {
      commit("FETCH_LAST_CUMULATIVE_RETURNS_SUCCESS", {
        period,
        data: null,
      });
      // We rethrow the error for sentry to catch it.
      throw error;
    }
  },
  async fetchPerformanceData(
    { commit },
    { productId, period, benchmarkSymbol, statIds, updateFrequency }
  ) {
    const params = {
      update_frequency: updateFrequency.id,
    };
    if (period) {
      params["period_start_date"] = period.start;
      params["period_end_date"] = period.end;
    }
    if (benchmarkSymbol !== undefined) {
      params["benchmark_symbol"] = benchmarkSymbol;
    }
    // Only compute the max range.
    params["periods"] = ["max"];
    if (statIds !== undefined) {
      params["stat_ids"] = statIds;
    }
    try {
      const response = await axios.get(`/api/products/${productId}/performance-data`, {
        params: params,
      });
      commit("FETCH_PERFORMANCE_DATA_SUCCESS", {
        period,
        data: response.data,
      });
    } catch (error) {
      commit("FETCH_PERFORMANCE_DATA_SUCCESS", {
        period,
        data: null,
      });
      // We rethrow the error for sentry to catch it.
      throw error;
    }
  },
  async fetchPerformanceWithPeriodsData(
    { commit },
    { productId, period, benchmarkSymbol, periods, statIds, updateFrequency }
  ) {
    const params = {
      update_frequency: updateFrequency.id,
    };
    if (period) {
      params["period_start_date"] = period.start;
      params["period_end_date"] = period.end;
    }
    if (benchmarkSymbol !== undefined) {
      params["benchmark_symbol"] = benchmarkSymbol;
    }
    if (periods !== undefined) {
      params["periods"] = periods;
    }
    if (statIds !== undefined) {
      params["stat_ids"] = statIds;
    }
    try {
      const response = await axios.get(`/api/products/${productId}/performance-data`, {
        params: params,
      });
      commit("FETCH_PERFORMANCE_WITH_PERIOD_DATA_SUCCESS", {
        period,
        data: response.data,
      });
    } catch (error) {
      commit("FETCH_PERFORMANCE_WITH_PERIOD_DATA_SUCCESS", {
        period,
        data: null,
      });
      // We rethrow the error for sentry to catch it.
      throw error;
    }
  },
  async fetchReturnsVar({ commit }, { productId, period, timeWindow, valueAtRisk }) {
    const params = {};
    if (period) {
      params["period_start_date"] = period.start;
      params["period_end_date"] = period.end;
    }
    if (timeWindow !== undefined) {
      params["time_window"] = timeWindow;
    }
    if (valueAtRisk !== undefined) {
      params["value_at_risk"] = valueAtRisk;
    }
    try {
      const response = await axios.get(`/api/products/${productId}/returns-var`, {
        params,
      });
      commit("FETCH_RETURN_VAR_SUCCESS", {
        data: response.data,
      });
    } catch (error) {
      commit("FETCH_RETURN_VAR_SUCCESS", {
        data: null,
      });
      throw error;
    }
  },
  async fetchRiskReturn({ commit }, { productId, period, benchmarkSymbol, updateFrequency }) {
    const params = {
      update_frequency: updateFrequency.id,
    };
    if (period) {
      params["period_start_date"] = period.start;
      params["period_end_date"] = period.end;
    }
    if (benchmarkSymbol !== undefined) {
      params["benchmark_symbol"] = benchmarkSymbol;
    }
    // Only compute the max range.
    params["periods"] = ["max"];
    // Only get the annualized return and volatility.
    params["stat_ids"] = [
      PerformanceStatistic.annualizedReturn.id,
      PerformanceStatistic.annualizedVolatility.id,
    ];
    try {
      const response = await axios.get(`/api/products/${productId}/performance-data`, {
        params: params,
      });
      commit("FETCH_RISK_RETURN_SUCCESS", {
        period,
        data: response.data,
      });
    } catch (error) {
      commit("FETCH_RISK_RETURN_SUCCESS", {
        period,
        data: null,
      });
      // We rethrow the error for sentry to catch it.
      throw error;
    }
  },
  async fetchCumulativeReturns(
    { commit },
    { productId, period, adjusted, benchmarkSymbol, initialValue, resamplingStrategy }
  ) {
    const params = {};
    if (period) {
      params["period_start_date"] = period.start;
      params["period_end_date"] = period.end;
    }
    if (initialValue) {
      params["initial_value"] = initialValue;
    }
    if (adjusted !== undefined) {
      params["adjusted"] = adjusted;
    }
    if (benchmarkSymbol !== undefined) {
      params["benchmark_symbol"] = benchmarkSymbol;
    }
    if (resamplingStrategy != undefined) {
      params["resampling_strategy"] = resamplingStrategy;
    }
    try {
      const response = await axios.get(`/api/products/${productId}/cumulative-returns`, {
        params: params,
      });
      commit("FETCH_CUMULATIVE_RETURNS_SUCCESS", {
        period,
        data: response.data,
      });
    } catch (error) {
      commit("FETCH_CUMULATIVE_RETURNS_SUCCESS", {
        period,
        data: null,
      });
      // We rethrow the error for sentry to catch it.
      throw error;
    }
  },
  async fetchProductPrices({ commit }, { productId }) {
    try {
      const response = await axios.get(`/api/products/${productId}/product-prices`);
      commit("FETCH_PRODUCT_PRICES_SUCCESS", {
        data: response.data,
      });
    } catch (error) {
      commit("FETCH_PRODUCT_PRICES_SUCCESS", {
        data: null,
      });
      // We rethrow the error for sentry to catch it.
      throw error;
    }
  },
  async fetchBenchmarkPrices({ commit }, { productId, benchmarkSymbol }) {
    const params = {};
    if (benchmarkSymbol !== undefined) {
      params["benchmark_symbol"] = benchmarkSymbol;
    }
    try {
      const response = await axios.get(`/api/products/${productId}/benchmark-prices`, {
        params: params,
      });
      commit("FETCH_BENCHMARK_PRICES_SUCCESS", {
        benchmarkSymbol,
        data: response.data,
      });
    } catch (error) {
      commit("FETCH_BENCHMARK_PRICES_SUCCESS", {
        benchmarkSymbol,
        data: null,
      });
      // We rethrow the error for sentry to catch it.
      throw error;
    }
  },
  async fetchYearlyCumulativeReturns({ commit }, { productId, benchmarkSymbol, period }) {
    const params = {};
    if (benchmarkSymbol !== undefined) {
      params["benchmark_symbol"] = benchmarkSymbol;
    }
    if (period) {
      params["period_start_date"] = period.start;
      params["period_end_date"] = period.end;
    }
    try {
      const response = await axios.get(`/api/products/${productId}/yearly-cumulative-returns`, {
        params: params,
      });
      commit("FETCH_YEARLY_CUMULATIVE_RETURNS_SUCCESS", {
        data: response.data,
      });
    } catch (error) {
      commit("FETCH_YEARLY_CUMULATIVE_RETURNS_SUCCESS", {
        data: null,
      });
      // We rethrow the error for sentry to catch it.
      throw error;
    }
  },
  async fetchMonthlyCumulativeReturns({ commit }, { productId, benchmarkSymbol, period }) {
    const params = {};
    if (benchmarkSymbol !== undefined) {
      params["benchmark_symbol"] = benchmarkSymbol;
    }
    if (period) {
      params["period_start_date"] = period.start;
      params["period_end_date"] = period.end;
    }
    try {
      const response = await axios.get(`/api/products/${productId}/monthly-cumulative-returns`, {
        params: params,
      });
      commit("FETCH_MONTHLY_CUMULATIVE_RETURNS_SUCCESS", {
        data: response.data,
      });
    } catch (error) {
      commit("FETCH_MONTHLY_CUMULATIVE_RETURNS_SUCCESS", {
        data: null,
      });
      // We rethrow the error for sentry to catch it.
      throw error;
    }
  },
  async fetchDrawdowns({ commit }, { productId, period, benchmarkSymbol, resamplingStrategy }) {
    const params = {};
    if (period) {
      params["period_start_date"] = period.start;
      params["period_end_date"] = period.end;
    }
    if (benchmarkSymbol !== undefined) {
      params["benchmark_symbol"] = benchmarkSymbol;
    }
    if (resamplingStrategy != undefined) {
      params["resampling_strategy"] = resamplingStrategy;
    }
    try {
      const response = await axios.get(`/api/products/${productId}/drawdowns`, {
        params: params,
      });
      commit("FETCH_DRAWDOWNS_SUCCESS", {
        data: response.data,
      });
    } catch (error) {
      commit("FETCH_DRAWDOWNS_SUCCESS", {
        data: null,
      });
      // We rethrow the error for sentry to catch it.
      throw error;
    }
  },
  async fetchBiggestDrawdowns(
    { commit },
    { productId, period, benchmarkSymbol, numberOfDrawdowns }
  ) {
    const params = {};
    if (period) {
      params["period_start_date"] = period.start;
      params["period_end_date"] = period.end;
    }
    if (benchmarkSymbol !== undefined) {
      params["benchmark_symbol"] = benchmarkSymbol;
    }
    if (numberOfDrawdowns !== undefined) {
      params["number_of_drawdowns"] = numberOfDrawdowns;
    }
    try {
      const response = await axios.get(`/api/products/${productId}/biggest-drawdowns`, {
        params: params,
      });
      commit("FETCH_BIGGEST_DRAWDOWNS_SUCCESS", {
        data: response.data,
      });
    } catch (error) {
      commit("FETCH_BIGGEST_DRAWDOWNS_SUCCESS", {
        data: null,
      });
      throw error;
    }
  },
  async fetchOutperformanceValues(
    { commit },
    { productId, period, benchmarkSymbol, resamplingStrategy }
  ) {
    const params = {};
    if (period) {
      params["period_start_date"] = period.start;
      params["period_end_date"] = period.end;
    }
    if (benchmarkSymbol !== undefined) {
      params["benchmark_symbol"] = benchmarkSymbol;
    }
    if (resamplingStrategy != undefined) {
      params["resampling_strategy"] = resamplingStrategy;
    }
    try {
      const response = await axios.get(`/api/products/${productId}/outperformance`, {
        params: params,
      });
      commit("FETCH_OUTPERFORMANCE_SUCCESS", {
        data: response.data,
      });
    } catch (error) {
      commit("FETCH_OUTPERFORMANCE_SUCCESS", {
        data: null,
      });
      // We rethrow the error for sentry to catch it.
      throw error;
    }
  },
  async fetchVolatility(
    { commit },
    { productId, period, benchmarkSymbol, resamplingStrategy, windowHorizon, updateFrequency }
  ) {
    const params = {
      update_frequency: updateFrequency.id,
    };
    if (period) {
      params["period_start_date"] = period.start;
      params["period_end_date"] = period.end;
    }
    if (benchmarkSymbol !== undefined) {
      params["benchmark_symbol"] = benchmarkSymbol;
    }
    if (resamplingStrategy != undefined) {
      params["resampling_strategy"] = resamplingStrategy;
    }
    if (windowHorizon != undefined) {
      params["window_horizon"] = windowHorizon;
    }
    try {
      const response = await axios.get(`/api/products/${productId}/volatility`, {
        params: params,
      });
      commit("FETCH_VOLATILITY_SUCCESS", {
        data: response.data,
      });
    } catch (error) {
      commit("FETCH_VOLATILITY_SUCCESS", {
        data: null,
      });
      // We rethrow the error for sentry to catch it.
      throw error;
    }
  },
  async fetchAnnualizedReturnRolling(
    { commit },
    { productId, period, benchmarkSymbol, resamplingStrategy, windowHorizon }
  ) {
    const params = {};
    if (period) {
      params["period_start_date"] = period.start;
      params["period_end_date"] = period.end;
    }
    if (benchmarkSymbol !== undefined) {
      params["benchmark_symbol"] = benchmarkSymbol;
    }
    if (resamplingStrategy != undefined) {
      params["resampling_strategy"] = resamplingStrategy;
    }
    if (windowHorizon != undefined) {
      params["window_horizon"] = windowHorizon;
    }
    try {
      const response = await axios.get(`/api/products/${productId}/annualized-return-rolling`, {
        params: params,
      });
      commit("FETCH_ANNUALIZED_RETURN_ROLLING_SUCCESS", {
        data: response.data,
      });
    } catch (error) {
      commit("FETCH_ANNUALIZED_RETURN_ROLLING_SUCCESS", {
        data: null,
      });
      throw error;
    }
  },
  async fetchMaxDrawdownRolling(
    { commit },
    { productId, period, benchmarkSymbol, resamplingStrategy, windowHorizon }
  ) {
    const params = {};
    if (period) {
      params["period_start_date"] = period.start;
      params["period_end_date"] = period.end;
    }
    if (benchmarkSymbol !== undefined) {
      params["benchmark_symbol"] = benchmarkSymbol;
    }
    if (resamplingStrategy != undefined) {
      params["resampling_strategy"] = resamplingStrategy;
    }
    if (windowHorizon != undefined) {
      params["window_horizon"] = windowHorizon;
    }
    try {
      const response = await axios.get(`/api/products/${productId}/max-drawdown-rolling`, {
        params: params,
      });
      commit("FETCH_MAX_DRAWDOWN_ROLLING_SUCCESS", {
        data: response.data,
      });
    } catch (error) {
      commit("FETCH_MAX_DRAWDOWN_ROLLING_SUCCESS", {
        data: null,
      });
      throw error;
    }
  },
  async fetchReturns({ commit }, { productId, period, benchmarkSymbol }) {
    const params = {};
    if (period) {
      params["period_start_date"] = period.start;
      params["period_end_date"] = period.end;
    }
    if (benchmarkSymbol !== undefined) {
      params["benchmark_symbol"] = benchmarkSymbol;
    }
    try {
      const response = await axios.get(`/api/products/${productId}/returns`, {
        params: params,
      });
      commit("FETCH_RETURNS_SUCCESS", {
        data: response.data,
      });
    } catch (error) {
      commit("FETCH_RETURNS_SUCCESS", {
        data: null,
      });
      // We rethrow the error for sentry to catch it.
      throw error;
    }
  },
  async fetchHoldingsDetails({ commit }, { productId }) {
    const params = {};
    try {
      const response = await axios.get(`/api/products/${productId}/holdings-details`, {
        params: params,
      });
      // If the response status is 204, it means we couldn't find any holdings data.
      if (response.status === 204) {
        commit("FETCH_HOLDINGS_DETAILS_SUCCESS", {
          data: undefined,
        });
      } else {
        commit("FETCH_HOLDINGS_DETAILS_SUCCESS", {
          data: response.data,
        });
      }
    } catch (error) {
      commit("FETCH_HOLDINGS_DETAILS_SUCCESS", {
        data: null,
      });
      // We rethrow the error for sentry to catch it.
      throw error;
    }
  },
  async fetchLiquidityAnalysisHoldings({ commit }, { productId, latestDate, lang, view }) {
    const params = {
      latest_date: latestDate,
      lang: lang,
      view: view,
      with_internal_properties: true,
    };
    try {
      const response = await axios.get(`/api/products/${productId}/holdings`, {
        params: params,
      });
      commit("FETCH_LIQUIDITY_ANALYSIS_HOLDINGS_SUCCESS", {
        data: response.data,
      });
    } catch (error) {
      commit("FETCH_LIQUIDITY_ANALYSIS_HOLDINGS_SUCCESS", {
        data: null,
      });
      // We rethrow the error for sentry to catch it.
      throw error;
    }
  },
  async fetchHoldingDataPropertyMetadata({ commit }, { productId, lang }) {
    const params = {};
    params["lang"] = lang;
    try {
      const response = await axios.get(
        `/api/products/${productId}/holding-data-property-metadata`,
        {
          params: params,
        }
      );
      // If the response status is 204, it means there is no property.
      if (response.status === 204) {
        commit("FETCH_HOLDING_DATA_PROPERTY_METADATA_SUCCESS", {
          data: undefined,
        });
      } else {
        commit("FETCH_HOLDING_DATA_PROPERTY_METADATA_SUCCESS", {
          data: response.data,
        });
      }
    } catch (error) {
      commit("FETCH_HOLDING_DATA_PROPERTY_METADATA_SUCCESS", {
        data: null,
      });
      // We rethrow the error for sentry to catch it.
      throw error;
    }
  },
  async fetchHoldingDataPropertyCategoriesTranslations(
    { commit },
    { productId, lang, availableLangs }
  ) {
    const params = {};
    params["lang"] = lang;
    params["available_langs"] = availableLangs;
    try {
      const response = await axios.get(
        `/api/products/${productId}/holding-data-property-categories-translations`,
        {
          params: params,
        }
      );
      // If the response status is 204, it means there is no property.
      if (response.status === 204) {
        commit("FETCH_HOLDING_DATA_PROPERTY_CATEGORIES_TRANSLATIONS_SUCCESS", {
          data: undefined,
        });
      } else {
        commit("FETCH_HOLDING_DATA_PROPERTY_CATEGORIES_TRANSLATIONS_SUCCESS", {
          data: response.data,
        });
      }
    } catch (error) {
      commit("FETCH_HOLDING_DATA_PROPERTY_CATEGORIES_TRANSLATIONS_SUCCESS", {
        data: null,
      });
      // We rethrow the error for sentry to catch it.
      throw error;
    }
  },
  async fetchHoldingsTable({ commit }, { productId, latestDate, lang, view, holdingComponentId }) {
    const params = {
      latest_date: latestDate,
      lang: lang,
      view: view,
      component_id: holdingComponentId,
    };
    try {
      const response = await axios.get(`/api/products/${productId}/holdings`, {
        params: params,
      });
      commit("FETCH_HOLDINGS_TABLE_SUCCESS", {
        data: response.data,
        holdingComponentId,
      });
    } catch (error) {
      commit("FETCH_HOLDINGS_TABLE_SUCCESS", {
        data: null,
        holdingComponentId,
      });
      // We rethrow the error for sentry to catch it.
      throw error;
    }
  },
  async transferHoldingsTable({ commit }, { fromHoldingComponentId, toHoldingComponentId }) {
    commit("TRANSFER_HOLDINGS_TABLE", { fromHoldingComponentId, toHoldingComponentId });
  },
  async fetchPortfolioBreakdownOnDate(
    { commit },
    { productId, holdingComponentId, lang, view, property, asOfDate, normalizeBreakdownWeight }
  ) {
    const params = {
      lang: lang,
      view: view,
      component_id: holdingComponentId,
      property_name: property,
      as_of_date: asOfDate,
      normalize_breakdown_weight: normalizeBreakdownWeight,
    };
    try {
      const response = await axios.get(
        `/api/products/${productId}/holdings/aggregate-by-property-on-date`,
        {
          params: params,
        }
      );
      commit("FETCH_PORTFOLIO_BREAKDOWN_ON_DATE_SUCCESS", {
        data: response.data,
        holdingComponentId,
      });
    } catch (error) {
      commit("FETCH_PORTFOLIO_BREAKDOWN_ON_DATE_SUCCESS", {
        data: null,
        holdingComponentId,
      });
      // We rethrow the error for sentry to catch it.
      throw error;
    }
  },
  async transferPortfolioBreakdownOnDate(
    { commit },
    { fromHoldingComponentId, toHoldingComponentId }
  ) {
    commit("TRANSFER_PORTFOLIO_BREAKDOWN_ON_DATE", {
      fromHoldingComponentId,
      toHoldingComponentId,
    });
  },
  async fetchPortfolioBreakdownOverTime(
    { commit },
    {
      productId,
      holdingComponentId,
      lang,
      view,
      property,
      resamplingStrategy,
      startDate,
      endDate,
      normalizeBreakdownWeight,
    }
  ) {
    const params = {
      lang: lang,
      view: view,
      component_id: holdingComponentId,
      property_name: property,
      start_date: startDate,
      end_date: endDate,
      resampling_strategy: resamplingStrategy,
      normalize_breakdown_weight: normalizeBreakdownWeight,
    };
    try {
      const response = await axios.get(
        `/api/products/${productId}/holdings/aggregate-by-property-over-time`,
        {
          params: params,
        }
      );
      commit("FETCH_PORTFOLIO_BREAKDOWN_OVER_TIME_SUCCESS", {
        data: response.data,
        holdingComponentId,
      });
    } catch (error) {
      commit("FETCH_PORTFOLIO_BREAKDOWN_OVER_TIME_SUCCESS", {
        data: null,
        holdingComponentId,
      });
      // We rethrow the error for sentry to catch it.
      throw error;
    }
  },
  async transferPortfolioBreakdownOverTime(
    { commit },
    { fromHoldingComponentId, toHoldingComponentId }
  ) {
    commit("TRANSFER_PORTFOLIO_BREAKDOWN_OVER_TIME", {
      fromHoldingComponentId,
      toHoldingComponentId,
    });
  },
  async fetchAssetsUnderManagement({ commit }, { productId, latestDate }) {
    const params = {};
    params["latest_date"] = latestDate;
    try {
      const response = await axios.get(`/api/products/${productId}/assets-under-management`, {
        params: params,
      });
      // If the response status is 204, it means the aum is not available.
      if (response.status === 204) {
        commit("FETCH_ASSETS_UNDER_MANAGEMENT_SUCCESS", {
          data: undefined,
        });
      } else {
        commit("FETCH_ASSETS_UNDER_MANAGEMENT_SUCCESS", {
          data: response.data,
        });
      }
    } catch (error) {
      commit("FETCH_ASSETS_UNDER_MANAGEMENT_SUCCESS", {
        data: null,
      });
      // We rethrow the error for sentry to catch it.
      throw error;
    }
  },
  async fetchPerformanceTableData({ commit }, { productId, updateFrequency }) {
    const params = {
      update_frequency: updateFrequency.id,
    };
    try {
      const response = await axios.get(`/api/products/${productId}/performance-table-data`, {
        params: params,
      });
      commit("FETCH_PERFORMANCE_TABLE_DATA_SUCCESS", {
        data: response.data,
      });
    } catch (error) {
      commit("FETCH_PERFORMANCE_TABLE_DATA_SUCCESS", {
        data: null,
      });
      // We rethrow the error for sentry to catch it.
      throw error;
    }
  },
  async fetchLastLivePrice({ commit }, { productId, latestDate }) {
    const params = {};
    if (latestDate !== undefined) {
      params["latest_date"] = latestDate;
    }
    try {
      const response = await axios.get(`/api/products/${productId}/last-live-price`, {
        params: params,
      });
      commit("FETCH_LAST_LIVE_PRICE_SUCCESS", {
        data: response.data,
      });
    } catch (error) {
      commit("FETCH_LAST_LIVE_PRICE_SUCCESS", {
        data: null,
      });
      // We rethrow the error for sentry to catch it.
      throw error;
    }
  },
  bindProductConfig: firestoreAction(({ bindFirestoreRef, state, commit }, { productId }) => {
    // Binding an already bound state is expensive, likely because of an internal implementation
    // that unbinds first and then rebinds. It's why we avoid rebinding if it is
    // already bound or in progress.
    if (state.productConfig != null || state.bindProductConfigPromise != null) {
      return state.bindProductConfigPromise || Promise.resolve();
    }

    const bindingPromise = bindFirestoreRef(
      "productConfig",
      firebase.firestore().collection("products").doc(productId)
    ).finally(() => {
      // Clear the promise when the binding completes.
      commit("SET_BIND_PRODUCT_CONFIG_PROMISE", null);
    });

    commit("SET_BIND_PRODUCT_CONFIG_PROMISE", bindingPromise);

    return bindingPromise;
  }),
  bindViewsConfig: firestoreAction(({ bindFirestoreRef, state, commit }, { productId, lang }) => {
    // Binding an already bound state is expensive, likely because of an internal implementation
    // that unbinds first and then rebinds. It's why we avoid rebinding if it is
    // already bound or in progress.
    if (
      state.viewsConfigPerLang[lang] != null ||
      state.bindViewsConfigPromisePerLang[lang] != null
    ) {
      return state.bindViewsConfigPromisePerLang[lang] || Promise.resolve();
    }

    // As we need to bind to an entry of an object, and Vuexfire doesn't support that out of the
    // box, we need to create a temporary state key to which we bind it, and then set it to the
    // correct entry using Vue.set (and also delete the temporary one to clean the state).
    const tempStateKey = `viewsConfigPerLang_${lang}`;
    state[tempStateKey] = undefined;
    const bindingPromise = bindFirestoreRef(
      tempStateKey,
      firebase
        .firestore()
        .collection("products")
        .doc(productId)
        .collection("langs")
        .doc(lang)
        .collection("views")
    )
      .then(() => {
        commit("SET_VIEWS_CONFIG_PER_LANG_FROM_TEMP_KEY", {
          lang,
          tempStateKey,
        });
      })
      .finally(() => {
        // Clear the promise when the binding completes.
        commit("SET_BIND_VIEWS_CONFIG_PER_LANG_PROMISE", { promise: null, lang });
      });

    commit("SET_BIND_VIEWS_CONFIG_PER_LANG_PROMISE", { promise: bindingPromise, lang });

    return bindingPromise;
  }),
  addAdditionalFollowers: firestoreAction((_, { productId, emails }) => {
    return firebase
      .firestore()
      .collection("products")
      .doc(productId)
      .update("additionalFollowers", firebase.firestore.FieldValue.arrayUnion(...emails));
  }),
  removeAdditionalFollower: firestoreAction((_, { productId, email }) => {
    return firebase
      .firestore()
      .collection("products")
      .doc(productId)
      .update("additionalFollowers", firebase.firestore.FieldValue.arrayRemove(email));
  }),
  resetAllStates({ commit }) {
    commit("RESET_ALL_STATES");
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
